import React, { useState, useEffect } from "react";
import "../../styles/common/login.scss";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { superAdminLogin } from "../../helper/apicalls/login";
import { institutionLogin, institutionForgotPasswordCall } from "../../helper/apicalls/institution/login";
import EdluminaLogo from "../../assets/png/edlumina-logo-1.png";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const p = location.pathname;
  const { setSearchParams } = useSearchParams();
  const [view, setView] = useState("login"); // 'login', 'forgot-password', 'verification-pending'
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotApiError, setForgotApiError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    const adminToken = Cookies.get("admin_token");
    const instToken = Cookies.get("institution_token");
    const teacherToken = Cookies.get("teacher_token");

    if (adminToken) {
      navigate("/admin/dashboard");
    } else if (teacherToken) {
      navigate("/teacher/dashboard");
    } else if (instToken) {
      navigate("/institution/dashboard");
    }
  }, [p, navigate]);

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      setForgotError("Please enter a valid email address.");
      return;
    }

    setForgotError("");
    setForgotApiError("");
    setForgotLoading(true);

    try {
      const url = window.location.origin;
      await institutionForgotPasswordCall(forgotEmail, url);
      setView("verification-pending");
    } catch (err) {
      console.error("Forgot password failed:", err);
      setForgotApiError(
        err.message ||
          "Failed to send reset link. Please check the email and try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const validateField = (name, value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    switch (name) {
      case "email":
        if (!value) return "Email is required.";
        if (!emailRegex.test(value))
          return "Please enter a valid email address.";
        return "";
      case "password":
        if (!value) return "Password is required.";
        if (value.length < 6) return "Password must be at least 6 characters.";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { email: form.email, password: form.password };
    const validationErrors = {
      email: validateField("email", payload.email),
      password: validateField("password", payload.password),
    };
    const hasErrors = Object.values(validationErrors).some((err) => err);
    if (hasErrors) {
      setErrors(validationErrors);
      return;
    }
    setIsLoading(true);
    if (p.startsWith("/admin/login")) {
      try {
        const res = await superAdminLogin(payload);
        // console.log("Admin login success");
        // console.log(res, "res");
        
        // Empty old cookies and storage
        const keys = Object.keys(Cookies.get());
        keys.forEach((k) => {
          Cookies.remove(k);
          Cookies.remove(k, { path: "/" });
        });
        localStorage.clear();
        sessionStorage.clear();

        Cookies.set("role", "admin");
        Cookies.set("admin_token", res?.auth_token);
        Cookies.set("name", res?.name);
        Cookies.set("email", res?.email);
        navigate("/admin/dashboard");
      } catch (err) {
        console.error("Admin login failed:", err);
        setErrors((prev) => ({
          ...prev,
          error: "Invalid email or password.",
        }));
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const res = await institutionLogin(payload);
        // console.log(res, "institution");
        
        // Empty old cookies and storage
        const keys = Object.keys(Cookies.get());
        keys.forEach((k) => {
          Cookies.remove(k);
          Cookies.remove(k, { path: "/" });
        });
        localStorage.clear();
        sessionStorage.clear();

        if (res?.role === "admin") {
          Cookies.set("role", "institution");
        } else {
          Cookies.set("role", res?.role);
        }
        Cookies.set("institution_token", res?.auth_token);
        Cookies.set("permissions", JSON.stringify(res?.permissions));
        if (res?.secret_key) {
          sessionStorage.setItem("secret_key", res.secret_key);
        }
        
        if (res?.role === "teacher") {
          Cookies.set("teacher_token", res?.auth_token);
          navigate("/teacher/dashboard");
        } else {
          navigate("institution/dashboard");
        }
      } catch (err) {
        console.error(err, "error");
        setErrors((prev) => ({
          ...prev,
          error: "Invalid email or password.",
        }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-logo-wrapper">
        <img src={EdluminaLogo} alt="Edlumina logo" className="login-logo" />
      </div>
      <div className="login-card" role="dialog" aria-labelledby="signin-title">
        {view === "login" && (
          <>
            <h2 id="signin-title" className="title">
              Sign In
            </h2>

            <form className="login-form" onSubmit={onSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  className={errors.email ? "error" : ""}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <span id="email-error" className="error-message">
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    value={form.password}
                    onChange={handleChange}
                    className={errors.password ? "error" : ""}
                    aria-invalid={!!errors.password}
                    aria-describedby={
                      errors.password ? "password-error" : undefined
                    }
                  />
                  <button
                    type="button"
                    className="toggle-visibility"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((s) => !s)}
                  >
                    {showPassword ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 5C6.5 5 2.1 8.6 1 12c1.1 3.4 5.5 7 11 7s9.9-3.6 11-7c-1.1-3.4-5.5-7-11-7z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="3.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="none"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 3l18 18"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M12 5C6.5 5 2.1 8.6 1 12c.49 1.5 1.67 3.1 3.36 4.41M20.64 16.41C22.33 15.1 23.51 13.5 24 12c-1.1-3.4-5.5-7-12-7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <path
                          d="M9.5 9.5a3.5 3.5 0 004.95 4.95"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="none"
                        />
                      </svg>
                    )}
                  </button>
                  {errors.password && (
                    <span id="password-error" className="error-message">
                      {errors.password}
                    </span>
                  )}
                </div>
              </div>

              {!p.startsWith("/admin/login") && (
                <div className="form-row">
                  <button
                    type="button"
                    className="forgot"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    onClick={() => {
                      setView("forgot-password");
                      setErrors({});
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
              {errors.error && <div className="form-error">{errors.error}</div>}

              <button type="submit" className="btn-primary" disabled={isLoading}>
                {isLoading ? "Loading..." : "Sign In"}
              </button>
            </form>
          </>
        )}

        {view === "forgot-password" && (
          <>
            <h2 id="forgot-title" className="title">
              Forgot Password
            </h2>
            <p style={{ fontSize: "14px", color: "var(--color-gray, #6b7280)", textAlign: "center", marginBottom: "20px" }}>
              Enter your email address to receive a password reset link.
            </p>
            <form className="login-form" onSubmit={handleForgotSubmit}>
              {forgotApiError && <div className="form-error">{forgotApiError}</div>}

              <div className="form-group">
                <label htmlFor="forgotEmail">Email</label>
                <input
                  id="forgotEmail"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={(e) => {
                    setForgotEmail(e.target.value);
                    setForgotError("");
                    setForgotApiError("");
                  }}
                  className={forgotError ? "error" : ""}
                  disabled={forgotLoading}
                />
                {forgotError && (
                  <span id="forgot-email-error" className="error-message">
                    {forgotError}
                  </span>
                )}
              </div>

              <button type="submit" className="btn-primary" disabled={forgotLoading}>
                {forgotLoading ? "Sending..." : "Send Reset Link"}
              </button>

              <button
                type="button"
                className="btn-primary"
                style={{
                  background: "transparent",
                  color: "var(--btn-primary-bg-color, #2257f2)",
                  border: "1px solid var(--btn-primary-bg-color, #2257f2)",
                  marginTop: "10px"
                }}
                onClick={() => {
                  setView("login");
                  setForgotEmail("");
                  setForgotError("");
                  setForgotApiError("");
                }}
                disabled={forgotLoading}
              >
                Back to Sign In
              </button>
            </form>
          </>
        )}

        {view === "verification-pending" && (
          <>
            <h2 id="pending-title" className="title" style={{ color: "#10b981" }}>
              Check your email
            </h2>
            <p style={{ fontSize: "14px", color: "var(--color-gray, #6b7280)", marginTop: "16px", lineHeight: "1.5", textAlign: "center" }}>
              We've sent a password reset link to <strong>{forgotEmail}</strong>. Please check your inbox and click the link to reset your password.
            </p>
            <button
              type="button"
              className="btn-primary"
              style={{ width: "100%", marginTop: "24px" }}
              onClick={() => {
                setView("login");
                setForgotEmail("");
                setForgotError("");
                setForgotApiError("");
              }}
            >
              Back to Sign In
            </button>
          </>
        )}

        {/* <p className="signup-hint">
          Don’t have an account? <a href="#">Sign up</a>
        </p> */}
      </div>
    </div>
  );
};

export default Login;
